import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Spinner from '../_utils/spinner';
import googleMapsHelper from './google-maps-helper';
import screenAndStyle from '../_utils/screen-and-style';
import UrlBuilder from '../_utils/url-building';
import { removeInlineSeo } from '../_utils/tools/seo-helpers';
import Button from '../_common/button';

const fetchJson = (url) => fetch(url)
  .then(async response => {
    if (response.ok) {
      return response.json();
    }

    return response.json()
      .then((body) => {
        const error = new Error('Response error');
        error.status = response.status;
        error.body = body;
        throw error;
      });
  });

class StoreSearch extends React.Component {
  constructor(props) {
    super(props);

    this.hasSearched = false;
    this.state = {
      resultsSize: undefined,
      address: '',
      showSearch: true
    };
    this.searchInput = null;
    this.setSearchInputRef = element => {
      this.searchInput = element
    };
  }

  handleInputAddressChange = (e) => {
    const searchTerm = e.target.value.trim();
    if (searchTerm) {
      this.setState({ address: searchTerm, error: null });
    }
  }

  handleInputAddressKeyPress = (e) => {
    if (e.key === 'Enter') {
      this.search();
    }
  }

  handleLocationUpdateClick = () => {
    this.search();
  }

  handleShowSearch = () => {
    this.setState({ showSearch: true }, () => {
      if (this.searchInput) {
        this.searchInput.focus();
        this.searchInput.select();
      }
    });
  }

  searchCurrentPosition = () => {
    // Do not set state to loading if in IE, as IE will not run the onError
    // callback if the user denies location permission.
    if (!screenAndStyle.isInternetExplorer()) {
      this.setState({ loading: true });
    }

    googleMapsHelper.getCurrentPostcode(
      this.changeAddress,
      () => this.setState({ loading: false })
    );
  }

  changeAddress = async (address) => {
    const { onAddressChanged, urls } = this.props;
    const { resultsAddress } = this.state;

    this.setState({ loading: true, error: null });

    if (address === resultsAddress) {
      this.setState({ showSearch: false, loading: false });
      return;
    }

    fetchJson(`${urls.nearest_stores}?${UrlBuilder.buildURLQuery({ address, limit: 20 })}`)
      .then(({ locations }) => {
        this.hasSearched = true;
        this.setState({
          address,
          resultsAddress: address,
          resultsSize: locations.length,
          showSearch: !locations.length,
          loading: false
        });

        if (locations.length) {
          const { latitude: lat, longitude: lng } = locations[0].coordinates

          onAddressChanged({ address, coordinates: { lat, lng }, locations });
        }
      }, (e) => {
        console.error(e); // eslint-disable-line no-console
        this.setState({
          error: "Something went wrong and your search couldn't be completed, please try again or try a different search",
          resultsSize: undefined,
          loading: false
        });
      });
  }

  search() {
    const { address } = this.state;

    if (address.length) {
      this.changeAddress(address);
    };
  }

  render() {
    const { loading, error, showSearch, address, resultsSize, resultsAddress } = this.state;
    const noResults = this.hasSearched && resultsSize === 0;

    if (loading) {
      return <Spinner />;
    }

    removeInlineSeo();

    if (showSearch) {
      return (
        <div className='store-search'>
          <h1 className='title'>Store Finder</h1>
          <label htmlFor='store-finder-search-input' className='info'>
            Update your location to find a nearby Walmart Photo Center
          </label>
          <div className='search-input'>
            <input
              id='store-finder-search-input'
              aria-describedby='search-hint search-error'
              aria-controls='store-list'
              autoComplete='off'
              ref={this.setSearchInputRef}
              defaultValue={address}
              onChange={this.handleInputAddressChange}
              onKeyPress={this.handleInputAddressKeyPress} />
            {error && <i className='fa fa-exclamation-circle text-danger' title={`Error searching for address '${address}'`} />}
            <p id='search-error' aria-live='assertive' className={classNames({ 'text-danger': error, 'text-warning': noResults })}>
              {noResults && `No stores found near '${resultsAddress}'`}
              {error}
            </p>
          </div>
          <p id='search-hint'>Enter ZIP code, city or state</p>
          <Button
            className='btn-link float-right update-location'
            onClick={this.handleLocationUpdateClick}
            text='Update Location'
            disabled={!address.length}
          />
        </div>
      );
    };

    return (
      <div className='store-search text-center'>
        <div className='results'>
          <div>
            Your Location:
            &nbsp;
            <Button className='btn-link reset-btn' onClick={this.handleShowSearch} text={resultsAddress} title='Start new search' />
          </div>
          <div>
            ({resultsSize} photo centers near {resultsAddress})
          </div>
        </div>
      </div>
    );
  }
}

StoreSearch.propTypes = {
  urls: PropTypes.shape({ nearest_stores: PropTypes.string.isRequired }).isRequired,
  onAddressChanged: PropTypes.func.isRequired,
}

export default StoreSearch;
